import React, { useEffect, useState, useRef } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Card, CardContent } from '@sunwisesoftware/sunwise-ui';
import { debounce, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { CustomPagination } from 'common/components';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';

import { getCountryOptions } from '../helpers';

import BulkOperationsCard from './BulkOperationsCard';
import ProductColumns from './ProductColumns';

const ListItems = ({
    countries,
    downloadFile,
    filterData,
    filterItems,
    isFetching,
    isFetchingCountries,
    items,
    paginationData,
    setFilterData,
}) => {
    const [rowCount, setRowCount] = useState(0);
    const [currentFilterData, setCurrentFilterData] = useState(filterData);

    const handleClickCountry = (countryName) =>
        setFilterData((prev) => ({ ...prev, countryName }));

    const handleClickPlan = (planName) =>
        setFilterData((prev) => ({ ...prev, planName }));

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeSearch = (event) =>
        setFilterData((prev) => ({
            ...prev,
            page: 0,
            searchText: event.target.value,
        }));

    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;

    const columns = ProductColumns();
    const getRowHeight = () => 'auto';
    const initialState = {
        pagination: { paginationModel: { pageSize: pageSizeOptions[0] } },
    };
    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));
    const onSortModelChange = (model) => {
        if (!model[0]) return;
        setFilterData((prev) => ({
            ...prev,
            orderBy: model[0].field.toLocaleLowerCase(),
            sortBy: model[0].sort,
        }));
    };
    const paginationModel = {
        page: paginationData?.current_page - 1 || 0,
        pageSize: filterData?.pageSize || pageSizeOptions[0],
    };
    const slots = { pagination: CustomPagination };
    const slotProps = { pagination: { pageSizeOptions } };

    useEffect(() => {
        if (isEmpty(paginationData)) return;
        setRowCount((prev) =>
            paginationData?.total_rows !== undefined
                ? paginationData?.total_rows
                : prev,
        );
    }, [paginationData?.total_rows]);

    useEffect(() => {
        if (isEqual(currentFilterData, filterData)) return;
        handleOnFilter(filterData);
        setCurrentFilterData(filterData);
    }, [filterData]);

    return (
        <>
            <BulkOperationsCard
                countryOptions={getCountryOptions(countries)}
                downloadFile={downloadFile}
                filterData={filterData}
                handleClickCountry={handleClickCountry}
                handleClickPlan={handleClickPlan}
                handleOnChangeSearch={handleOnChangeSearch}
                isFetchingCountries={isFetchingCountries}
            />

            <Card>
                <CardContent>
                    <DataGrid
                        columns={columns}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        editMode="row"
                        getRowHeight={getRowHeight}
                        initialState={initialState}
                        loading={isFetching}
                        onPaginationModelChange={onPaginationModelChange}
                        onSortModelChange={onSortModelChange}
                        pageSizeOptions={pageSizeOptions}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        rowCount={rowCount}
                        rows={items}
                        slotProps={slotProps}
                        slots={slots}
                        sortingMode="server"
                    />
                </CardContent>
            </Card>
        </>
    );
};

ListItems.propTypes = {
    countries: PropTypes.array,
    downloadFile: PropTypes.func,
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    isFetching: PropTypes.bool,
    isFetchingCountries: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    setFilterData: PropTypes.func,
};

export default ListItems;
